/**
 * use-running-timer — Phase 3c TanStack Query hooks for the running timer.
 *
 *   useRunningTimer()  → useQuery on ['timer', 'running']; staleTime 30s.
 *                        Returns the current user's running entry, or null.
 *                        retry: false (matches the FE convention used by
 *                        useMe / useBoard — a 401 is "go home", not a
 *                        spinner-loop).
 *
 *   useStartTimer()    → useMutation; POST /api/timer/start. Optimistic:
 *                        synthesize a running entry into the cache; rollback
 *                        on error. Invalidates on settle so the BE-stamped
 *                        ids/timestamps replace the placeholder.
 *
 *   useStopTimer()     → useMutation; POST /api/timer/stop. Optimistic:
 *                        clear the running cache. Invalidate on settle plus
 *                        ['time-entries'] so the timesheet view (FE-2's
 *                        territory) refreshes once it lands.
 *
 * The realtime layer (`useRealtimeBoard`) invalidates the same keys on
 * `timer.started` / `timer.stopped` frames, so a timer started from another
 * tab pulls into this query without manual coordination.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { TimeEntry, TimerStartArgs, TimerStopReason } from '@/api/types-bridge'
import { useToast } from '@/components/ToastProvider'
import { useIsGuest } from '@/features/me/guest'
import { useMe } from '@/features/me/use-me'
import { AVAILABLE_WORK_QUERY_ROOT } from '@/features/time/use-available-work'

export const RUNNING_TIMER_QUERY_KEY = ['timer', 'running'] as const
export const TIME_ENTRIES_QUERY_KEY = ['time-entries'] as const
/**
 * Sibling cache families that also hold timer-derived data — the day /
 * week calendar grid (`useTimesheet`) and the per-day status badges
 * (`useTimesheetDays`). Mirrored here (rather than imported) so this
 * file doesn't take a circular dependency on `use-time-entries.ts`,
 * which itself imports from this file. Keep in sync with the constants
 * declared in `use-time-entries.ts`.
 */
const TIMESHEET_FAMILY = ['timesheet'] as const
const TIMESHEET_DAYS_FAMILY = ['timesheet-days'] as const

/**
 * Invalidate every cache that can change when a timer stops: the running
 * entry, the entries list, the calendar grid, and the per-day status
 * badges. ONE stop can come from three places — the Stop button, the BE
 * auto-stopping the prior timer when a second one starts, and the 8h
 * cap — so the stop mutation, the START mutation, and the WS
 * `timer.stopped` handlers all call this rather than each remembering
 * the four families. Forgetting the calendar families is how an
 * auto-stopped entry used to stay invisible until a manual refresh.
 */
export function invalidateTimerDerivedCaches(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: RUNNING_TIMER_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: TIME_ENTRIES_QUERY_KEY })
  queryClient.invalidateQueries({ queryKey: TIMESHEET_FAMILY })
  queryClient.invalidateQueries({ queryKey: TIMESHEET_DAYS_FAMILY })
  // Each rail row carries `hasActiveTimer`.
  queryClient.invalidateQueries({ queryKey: AVAILABLE_WORK_QUERY_ROOT })
}

/* -------------------------------------------------------------------------- */
/*                              useRunningTimer                               */
/* -------------------------------------------------------------------------- */

interface UseRunningTimerArgs {
  /** Pause when signed-out / route doesn't need it. Defaults to true. */
  enabled?: boolean
}

export function useRunningTimer({ enabled = true }: UseRunningTimerArgs = {}) {
  // Time tracking is closed to guests outright — every route in the
  // context answers 403 `guest_forbidden`, reads included. Without this
  // gate the poll fires on every page a guest opens and is the ONLY
  // console error in an otherwise silent session, which trains a reader
  // to ignore the console on exactly the account whose ceilings need
  // watching.
  const isGuest = useIsGuest()
  // `useIsGuest` reads not-guest while `/api/me` is in flight, which let one
  // request through before the role resolved; wait for the role.
  const meResolved = useMe().data != null

  return useQuery<TimeEntry | null>({
    queryKey: RUNNING_TIMER_QUERY_KEY,
    queryFn: async () => {
      // BE wraps every response in `{ data: ... }` — `data` may be null when
      // no timer is running. Unwrap so consumers compare against the entry
      // shape directly.
      const res = await apiClient<{ data: TimeEntry | null }>('/api/timer/running')
      return res.data
    },
    enabled: enabled && meResolved && !isGuest,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/* -------------------------------------------------------------------------- */
/*                              useStartTimer                                 */
/* -------------------------------------------------------------------------- */

interface StartTimerContext {
  previous: TimeEntry | null | undefined
}

/**
 * Build a placeholder running entry for the optimistic cache write. The
 * temp id is overwritten when the server response lands; everything else
 * is best-effort guesses until the real row arrives.
 */
function buildOptimisticRunningEntry(args: TimerStartArgs): TimeEntry {
  const now = new Date().toISOString()
  return {
    id: `temp-${Date.now()}`,
    tenantId: '',
    userId: '',
    deliverableKind: args.deliverableKind ?? null,
    deliverableId: args.deliverableId ?? null,
    projectId: args.projectId ?? null,
    startedAt: now,
    endedAt: null,
    actualStartedAt: now,
    actualEndedAt: null,
    notes: args.notes ?? '',
    // Match the BE default (5min) so the optimistic placeholder satisfies
    // the schema's `min(1)` constraint. The real value lands via the
    // server response in `onSuccess` and replaces this row anyway.
    snapMinutes: args.snapMinutes ?? 5,
    // New timer entries start `pending` — user must click the green
    // check on the calendar to approve before the day can be submitted.
    status: 'pending',
    // Fresh timer entries always belong to a `pending` day-timesheet.
    timesheetStatus: 'pending',
    shorthand: null,
    deliverableTitle: null,
    createdAt: now,
    updatedAt: now,
  }
}

export function useStartTimer() {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TimeEntry, Error, TimerStartArgs, StartTimerContext>({
    mutationFn: async (args) => {
      const res = await apiClient<{ data: TimeEntry }>('/api/timer/start', {
        method: 'POST',
        body: args,
      })
      return res.data
    },
    onMutate: async (args) => {
      await queryClient.cancelQueries({ queryKey: RUNNING_TIMER_QUERY_KEY })
      const previous = queryClient.getQueryData<TimeEntry | null>(RUNNING_TIMER_QUERY_KEY)
      queryClient.setQueryData<TimeEntry | null>(
        RUNNING_TIMER_QUERY_KEY,
        buildOptimisticRunningEntry(args),
      )
      return { previous }
    },
    onError: (_err, _args, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(RUNNING_TIMER_QUERY_KEY, context.previous ?? null)
      }
      toast.error("Couldn't start timer — please try again.")
    },
    onSuccess: (created) => {
      // Replace the temp placeholder with the real row in-place so the
      // header indicator's elapsed time doesn't snap forward when the
      // settle invalidation lands.
      queryClient.setQueryData<TimeEntry | null>(RUNNING_TIMER_QUERY_KEY, created)
    },
    onSettled: () => {
      // Starting can also STOP the previously running timer (BE overlap
      // rule), which creates a real entry on the calendar — refresh the
      // same families a stop does.
      invalidateTimerDerivedCaches(queryClient)
    },
  })
}

/* -------------------------------------------------------------------------- */
/*                              useStopTimer                                  */
/* -------------------------------------------------------------------------- */

interface StopTimerContext {
  previous: TimeEntry | null | undefined
}

export function useStopTimer() {
  const queryClient = useQueryClient()
  const toast = useToast()

  // The cap-auto-stop info toast is fired from `useRealtimeBoard`'s
  // `timer.stopped` handler when `reason === 'cap'`. The HTTP response
  // doesn't carry a reason — server-side cap auto-stops produce a
  // `timer.stopped` WS frame the user receives via the realtime
  // subscription. Both manual and cap-driven stops route through this
  // mutation; only the WS-side handler surfaces the cap toast.
  return useMutation<TimeEntry, Error, void, StopTimerContext>({
    mutationFn: async () => {
      const res = await apiClient<{ data: TimeEntry }>('/api/timer/stop', {
        method: 'POST',
      })
      return res.data
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: RUNNING_TIMER_QUERY_KEY })
      const previous = queryClient.getQueryData<TimeEntry | null>(RUNNING_TIMER_QUERY_KEY)
      queryClient.setQueryData<TimeEntry | null>(RUNNING_TIMER_QUERY_KEY, null)
      return { previous }
    },
    onError: (_err, _args, context) => {
      if (context !== undefined) {
        queryClient.setQueryData(RUNNING_TIMER_QUERY_KEY, context.previous ?? null)
      }
      toast.error('Failed to stop timer.')
    },
    onSettled: () => {
      invalidateTimerDerivedCaches(queryClient)
    },
  })
}
